import { useEffect, useRef, useState } from 'react';
import { COLOR_HEX } from '../videoEditor/editorControlValues';
import { contentBox, strokeHit, type MatchStroke, type MatchTool, type Panel } from '../../utils/roomMatchSync';
import type { DrawColor } from '../VideoCanvasOverlay';

/**
 * The drawings over one panel of the room's match video. Vector, in the
 * video's own content box (0 to 1 on each axis), so the coach's arrow lands
 * on the same racket on the player's screen whatever either window's size.
 * The coach draws with the marker or the arrow, and erases with the eraser
 * (decision 102: no circle; an old circle still shows, and erases); the
 * player only sees. Rendered as SVG at the content box's pixel size, so arrowheads and
 * circles keep their shape.
 */

const STROKE_PX = 4;

function newId(): string {
  return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

function StrokeShape({ stroke, w, h }: { stroke: MatchStroke; w: number; h: number }) {
  const hex = COLOR_HEX[stroke.color as DrawColor] ?? COLOR_HEX.red;
  const pts = stroke.points.map(([x, y]) => [x * w, y * h] as const);
  const common = { stroke: hex, strokeWidth: STROKE_PX, fill: 'none', strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  if (stroke.tool === 'brush') {
    if (pts.length === 1) return <circle cx={pts[0][0]} cy={pts[0][1]} r={STROKE_PX / 2} fill={hex} />;
    return <polyline points={pts.map(([x, y]) => `${x},${y}`).join(' ')} {...common} />;
  }
  if (pts.length < 2) return null;
  const [[x1, y1], [x2, y2]] = [pts[0], pts[pts.length - 1]];
  if (stroke.tool === 'circle') {
    return <ellipse cx={(x1 + x2) / 2} cy={(y1 + y2) / 2} rx={Math.abs(x2 - x1) / 2} ry={Math.abs(y2 - y1) / 2} {...common} />;
  }
  // Arrow: the shaft and a head of two 16px wings at 28 degrees.
  const angle = Math.atan2(y2 - y1, x2 - x1);
  const wing = (a: number) => `${x2 - 16 * Math.cos(angle - a)},${y2 - 16 * Math.sin(angle - a)}`;
  return (
    <g>
      <line x1={x1} y1={y1} x2={x2} y2={y2} {...common} />
      <polyline points={`${wing(0.49)} ${x2},${y2} ${wing(-0.49)}`} {...common} />
    </g>
  );
}

/** The eraser's reach, in pixels of the picture. */
const ERASER_PX = 14;

export function RoomMatchDrawLayer({ panel, video, strokes, tool, color, onStroke, erasing = false, onErase }: {
  panel: Panel;
  /** The panel's video, for its intrinsic size. */
  video: HTMLVideoElement | null;
  strokes: MatchStroke[];
  /** The coach's tool; null when not drawing (and always for the player). */
  tool: MatchTool | null;
  color: DrawColor;
  /** Every change to a stroke being drawn, start to finish. */
  onStroke?: (stroke: MatchStroke) => void;
  /** The eraser is the coach's tool: a press or drag over a stroke removes it. */
  erasing?: boolean;
  onErase?: (id: string) => void;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0, vw: 0, vh: 0 });
  const drawing = useRef<MatchStroke | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const read = () => setSize({ w: host.clientWidth, h: host.clientHeight, vw: video?.videoWidth ?? 0, vh: video?.videoHeight ?? 0 });
    read();
    const ro = new ResizeObserver(read);
    ro.observe(host);
    video?.addEventListener('loadedmetadata', read);
    return () => { ro.disconnect(); video?.removeEventListener('loadedmetadata', read); };
  }, [video]);

  const box = contentBox(size.w, size.h, size.vw, size.vh);
  const at = (e: React.PointerEvent): [number, number] | null => {
    const r = hostRef.current?.getBoundingClientRect();
    if (!r || !box.w || !box.h) return null;
    const x = (e.clientX - r.left - box.x) / box.w;
    const y = (e.clientY - r.top - box.y) / box.h;
    return [Math.max(0, Math.min(1, x)), Math.max(0, Math.min(1, y))];
  };

  const canErase = erasing && !!onErase;
  const editable = (!!tool && !!onStroke) || canErase;
  const erasingNow = useRef(false);
  const eraseAt = (e: React.PointerEvent) => {
    const p = at(e);
    if (!p || !box.w) return;
    const tol = ERASER_PX / Math.max(box.w, box.h);
    for (const s of strokes) if (strokeHit(s, p, tol)) onErase?.(s.id);
  };
  return (
    <div ref={hostRef} className="absolute inset-0" style={{ touchAction: editable ? 'none' : undefined, cursor: editable ? 'crosshair' : undefined, pointerEvents: editable ? 'auto' : 'none' }}
      data-match-draw-layer={panel}
      onPointerDown={(e) => {
        if (canErase) { e.currentTarget.setPointerCapture(e.pointerId); erasingNow.current = true; eraseAt(e); return; }
        if (!editable || !tool) return;
        const p = at(e);
        if (!p) return;
        e.currentTarget.setPointerCapture(e.pointerId);
        drawing.current = { id: newId(), panel, tool, color, points: tool === 'brush' ? [p] : [p, p] };
        onStroke?.(drawing.current);
      }}
      onPointerMove={(e) => {
        if (erasingNow.current) { eraseAt(e); return; }
        const s = drawing.current;
        const p = s && at(e);
        if (!s || !p) return;
        if (s.tool === 'brush') {
          const [lx, ly] = s.points[s.points.length - 1];
          if (Math.hypot(p[0] - lx, p[1] - ly) < 0.002) return;
          s.points = [...s.points, [Math.round(p[0] * 10000) / 10000, Math.round(p[1] * 10000) / 10000]];
        } else {
          s.points = [s.points[0], p];
        }
        drawing.current = { ...s };
        onStroke?.(drawing.current);
      }}
      onPointerUp={() => { drawing.current = null; erasingNow.current = false; }}
      onPointerCancel={() => { drawing.current = null; erasingNow.current = false; }}
    >
      {box.w > 0 && (
        <svg width={box.w} height={box.h} className="absolute" style={{ left: box.x, top: box.y, overflow: 'visible' }} aria-hidden="true">
          {strokes.map((s) => <StrokeShape key={s.id} stroke={s} w={box.w} h={box.h} />)}
        </svg>
      )}
    </div>
  );
}
